/**
 * Trainer dashboard — rebuilt from scratch, structure and surface both.
 *
 * The old page opened with seven KPI tiles and buried the day's teaching
 * three sections down. A trainer opens this to answer, in this order:
 *
 *   1. What am I teaching today, and where?
 *   2. Which of my batches is in trouble?
 *   3. How am I doing overall?
 *   4. Let me go find a batch / student / attendance record.
 *
 * So the page is ordered that way: today first, problems second, numbers
 * third, and the deep views behind task-named tabs. Nothing here imports
 * react-bootstrap or the vendor stylesheet.
 */

import { useState } from 'react';
import {
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  LayoutGrid,
  MonitorDot,
  TriangleAlert,
  Users,
} from 'lucide-react';

import '../../../../design/tokens.css';
import Console from './Console';
import {
  Badge,
  Card,
  Chip,
  Empty,
  Field,
  Input,
  Loading,
  Meter,
  Pager,
  Range,
  RangeSep,
  Section,
  Select,
  Tabs,
  Vital,
  VitalGrid,
} from './ui';
import {
  formatDate,
  timelineProgress,
  useAttendanceRoster,
  useTrainerDashboard,
} from './useTrainerDashboard';

const TABS = [
  { key: 'today', label: 'Console', icon: <MonitorDot size={17} /> },
  { key: 'batches', label: 'Teaching load', icon: <LayoutGrid size={17} /> },
  { key: 'attendance', label: 'Attendance', icon: <ClipboardCheck size={17} /> },
];

const bandTone = (rate) =>
  rate === null || rate === undefined ? 'sky'
    : rate >= 85 ? 'good'
    : rate >= 60 ? 'warn'
    : 'bad';

export default function TrainerDashboard({ trainerId, trainerName }) {
  const [tab, setTab] = useState('today');
  const [batchFilter, setBatchFilter] = useState('active');
  const [batchSearch, setBatchSearch] = useState('');

  const { trainer, stats, insights, view, loading, error } = useTrainerDashboard(trainerId);
  const roster = useAttendanceRoster(trainerId, tab === 'attendance');

  if (loading) {
    return (
      <div className="td">
        <Loading>Loading your teaching week…</Loading>
      </div>
    );
  }

  if (error) {
    return (
      <div className="td">
        <Empty icon={<TriangleAlert size={38} />} title="Could not load the dashboard">
          {error}
        </Empty>
      </div>
    );
  }

  const filteredBatches = (
    batchFilter === 'active' ? view.active
      : batchFilter === 'upcoming' ? view.upcoming
      : view.completed
  ).filter((b) => {
    const q = batchSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(b.name || '').toLowerCase().includes(q) ||
      String(b.courseName || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="td">
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'today' && (
        <Console
          trainerName={trainerName}
          trainer={trainer}
          stats={stats}
          insights={insights}
          view={view}
          onOpenTab={setTab}
        />
      )}

      {/* ═══════════════════ TEACHING LOAD ═══════════════════ */}
      {tab === 'batches' && (
        <>
          <Section
            title="Teaching load"
            stamp={`${filteredBatches.length} ${batchFilter}`}
          />

          <div className="td-filters">
            <Field label="Search">
              <Input
                type="text"
                placeholder="Batch or course name"
                value={batchSearch}
                onChange={(e) => setBatchSearch(e.target.value)}
              />
            </Field>
            <Field label="Status">
              <Select
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                options={[
                  { value: 'active', label: `Running (${view.active.length})` },
                  { value: 'upcoming', label: `Upcoming (${view.upcoming.length})` },
                  { value: 'completed', label: `Completed (${view.completed.length})` },
                ]}
              />
            </Field>
          </div>

          {filteredBatches.length === 0 ? (
            <Card>
              <Empty icon={<LayoutGrid size={34} />} title="Nothing here">
                {batchSearch ? 'No batch matches that search.' : `You have no ${batchFilter} batches.`}
              </Empty>
            </Card>
          ) : (
            <div className="td-grid td-grid-2">
              {filteredBatches.map((b) => {
                const s = insights?.batchStats?.[String(b.id)];
                const progress = timelineProgress(b.startTime, b.endTime);
                return (
                  <Card key={b.id || b._id} title={b.name} icon={<GraduationCap size={17} />}>
                    <div className="td-row">
                      <div className="td-row-main">
                        <div className="td-row-meta">
                          <span>{b.courseName}</span>
                          {b.mode && <Chip>{b.mode}</Chip>}
                        </div>
                        <div className="td-row-note">
                          {formatDate(b.startTime)} – {formatDate(b.endTime)}
                          {b.classroomName && b.classroomName !== 'Not assigned' && (
                            <> · {b.classroomName}</>
                          )}
                        </div>
                        <div style={{ marginTop: 12, display: 'grid', gap: 5 }}>
                          <Meter label="Timeline" value={progress} tone="sky" />
                          {s?.syllabusProgress !== null && s?.syllabusProgress !== undefined && (
                            <Meter label="Syllabus" value={s.syllabusProgress} tone="warn" />
                          )}
                        </div>
                      </div>
                      <div className="td-row-side">
                        <Badge tone="indigo">{b.studentCount ?? s?.enrolled ?? 0} students</Badge>
                        <Badge tone={bandTone(s?.attendanceRate)}>
                          {s?.attendanceRate === null || s?.attendanceRate === undefined
                            ? 'No sessions'
                            : `${s.attendanceRate}% attendance`}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════════════════ ATTENDANCE ═══════════════════ */}
      {tab === 'attendance' && (
        <>
          <Section
            title="Who turned up"
            stamp="one row per student per batch"
            action={roster.activeFilterCount > 0 ? `Clear ${roster.activeFilterCount} filters` : undefined}
            onAction={roster.resetFilters}
          />

          {roster.summary && (
            <VitalGrid>
              <Vital icon={<Users />} tone="indigo" value={roster.summary.students} label="Students" meter={100} />
              <Vital icon={<LayoutGrid />} tone="sky" value={roster.summary.batches} label="Batches" meter={100} />
              <Vital
                icon={<CheckCircle2 />}
                tone={roster.summary.averageRate >= 75 ? 'good' : 'warn'}
                value={roster.summary.averageRate === null ? '—' : `${roster.summary.averageRate}%`}
                label="Average attendance"
                meter={roster.summary.averageRate ?? 0}
                hint={`${roster.summary.sessions} sessions held`}
              />
              <Vital
                icon={<TriangleAlert />}
                tone="bad"
                value={roster.summary.critical}
                label="Below 50%"
                meter={roster.summary.rows ? Math.round((roster.summary.critical / roster.summary.rows) * 100) : 0}
                hint="Click the band filter to isolate"
              />
            </VitalGrid>
          )}

          <div className="td-filters">
            <Field label="Search student">
              <Input
                type="text"
                placeholder="Name, email or phone"
                value={roster.filters.search}
                onChange={(e) => roster.setFilter('search', e.target.value)}
              />
            </Field>
            <Field label="Batch">
              <Select
                value={roster.filters.batchId}
                onChange={(e) => roster.setFilter('batchId', e.target.value)}
                placeholder="All batches"
                options={roster.options.batches.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Field>
            <Field label="Course">
              <Select
                value={roster.filters.courseName}
                onChange={(e) => roster.setFilter('courseName', e.target.value)}
                placeholder="All courses"
                options={roster.options.courses}
              />
            </Field>
            <Field label="Batch status">
              <Select
                value={roster.filters.batchStatus}
                onChange={(e) => roster.setFilter('batchStatus', e.target.value)}
                placeholder="Any"
                options={[
                  { value: 'active', label: 'Running' },
                  { value: 'upcoming', label: 'Upcoming' },
                  { value: 'completed', label: 'Completed' },
                ]}
              />
            </Field>
            <Field label="Mode">
              <Select
                value={roster.filters.mode}
                onChange={(e) => roster.setFilter('mode', e.target.value)}
                placeholder="Any mode"
                options={roster.options.modes}
              />
            </Field>
            <Field label="Attendance band">
              <Select
                value={roster.filters.band}
                onChange={(e) => roster.setFilter('band', e.target.value)}
                placeholder="Any band"
                options={[
                  { value: 'critical', label: 'Critical — below 50%' },
                  { value: 'weak', label: 'Weak — 50 to 74%' },
                  { value: 'ok', label: 'OK — 75 to 84%' },
                  { value: 'strong', label: 'Strong — 85%+' },
                  { value: 'none', label: 'No sessions held yet' },
                ]}
              />
            </Field>
            <Field label="Sessions between">
              <Range>
                <Input type="date" value={roster.filters.dateFrom} onChange={(e) => roster.setFilter('dateFrom', e.target.value)} aria-label="Sessions on or after" />
                <RangeSep />
                <Input type="date" value={roster.filters.dateTo} onChange={(e) => roster.setFilter('dateTo', e.target.value)} aria-label="Sessions on or before" />
              </Range>
            </Field>
            <Field label="Attendance % between">
              <Range>
                <Input type="number" min="0" max="100" placeholder="min" value={roster.filters.minRate} onChange={(e) => roster.setFilter('minRate', e.target.value)} aria-label="Minimum percent" />
                <RangeSep />
                <Input type="number" min="0" max="100" placeholder="max" value={roster.filters.maxRate} onChange={(e) => roster.setFilter('maxRate', e.target.value)} aria-label="Maximum percent" />
              </Range>
            </Field>
            <Field label="Show only">
              <Select
                value={roster.filters.tail}
                onChange={(e) => roster.setFilter('tail', e.target.value)}
                placeholder="Everyone"
                options={[
                  { value: 'never', label: 'Never attended' },
                  { value: 'perfect', label: 'Perfect attendance' },
                ]}
              />
            </Field>
            <Field label="Sort by">
              <Select
                value={roster.filters.sort}
                onChange={(e) => roster.setFilter('sort', e.target.value)}
                options={[
                  { value: 'rate-asc', label: 'Attendance — lowest first' },
                  { value: 'rate-desc', label: 'Attendance — highest first' },
                  { value: 'absent-desc', label: 'Most absences' },
                  { value: 'attended-desc', label: 'Most attended' },
                  { value: 'name-asc', label: 'Name A–Z' },
                  { value: 'batch-asc', label: 'Batch name' },
                ]}
              />
            </Field>
          </div>

          {roster.loading ? (
            <Loading>Working out who attended…</Loading>
          ) : roster.rows.length === 0 ? (
            <Card>
              <Empty icon={<ClipboardCheck size={34} />} title="No attendance to show">
                {roster.activeFilterCount > 0
                  ? 'No student matches these filters. Try widening the date range.'
                  : 'No sessions have been marked for your batches yet.'}
              </Empty>
            </Card>
          ) : (
            <>
              <div className="td-table-wrap">
                <table className="td-table">
                  <thead>
                    <tr>
                      <th className="td-num">#</th>
                      <th>Student</th>
                      <th>Batch</th>
                      <th>Course</th>
                      <th className="td-num">Sessions</th>
                      <th className="td-num">Present</th>
                      <th className="td-num">Absent</th>
                      <th style={{ minWidth: 180 }}>Attendance</th>
                      <th>Last present</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.rows.map((r, i) => (
                      <tr key={`${r.studentId}-${r.batchId}`}>
                        <td className="td-num">{(roster.pagination.page - 1) * roster.pagination.limit + i + 1}</td>
                        <td>
                          <div className="td-strong">{r.name}</div>
                          {(r.email || r.phone) && (
                            <div className="td-muted">{[r.email, r.phone].filter(Boolean).join(' · ')}</div>
                          )}
                        </td>
                        <td>
                          <div className="td-strong">{r.batchName}</div>
                          {r.mode && <div className="td-muted">{r.mode}</div>}
                        </td>
                        <td className="td-muted">{r.courseName}</td>
                        <td className="td-num">{r.sessions}</td>
                        <td className="td-num td-good-ink">{r.attended}</td>
                        <td className="td-num td-bad-ink">{r.absent}</td>
                        <td>
                          {r.rate === null
                            ? <Chip>No sessions</Chip>
                            : <Meter value={r.rate} tone={r.band === 'strong' ? 'good' : r.band === 'ok' ? 'sky' : r.band === 'weak' ? 'warn' : 'bad'} />}
                        </td>
                        <td className="td-muted">{r.lastPresentAt ? formatDate(r.lastPresentAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager
                page={roster.pagination.page}
                totalPages={roster.pagination.totalPages}
                total={roster.pagination.total}
                limit={roster.pagination.limit}
                onPage={roster.goToPage}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
